'use client';
import { useCallback, useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import {
  LockClosed,
  Speedometer,
  Bulb,
  BusinessOutline,
  ChevronForward
} from '@raresail/react-ionicons';
import {
  goToCodeSignIn,
  signInFeatureItems,
  TermsLink,
  PrivacyLink
} from '../shared';
import { AccountField } from '../account-field';
import { GoogleButton } from '@/components/shared/google-button';
import { Button } from '@/components/shared/button';
import { useTranslation } from '@/contexts/language-context';
import { emailValidation } from '@/lib/const';
import { checkAccountExists, checkLogin, google, newAccount } from '../actions';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePartnerLoginDialog } from '@/contexts/partner-login-dialog';
import { Heading } from '@/components/shared/heading';
import { cleanPhoneNumber } from '@/lib/utils';
import { Features } from '@/components/shared/features';
import { Card } from '@/components/shared/card';
import Link from 'next/link';

export function NewUserForm({
  account = '',
  error,
  callbackUrl,
  isFinancing = false
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const partnerLogin = usePartnerLoginDialog();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { control, handleSubmit } = useForm({
    defaultValues: { account }
  });

  // Tracks whether the entered email/phone already has an account, so the
  // primary button can switch to "Sign In". The lookup is read-only and
  // server-side throttled (see checkAccountExists) to limit enumeration.
  const [accountStatus, setAccountStatus] = useState('unknown');
  const checkSeq = useRef(0);

  const isCompleteAccount = useCallback((value) => {
    const val = value?.trim() ?? '';
    if (!val) return false;
    return (
      cleanPhoneNumber(val).length === 10 ||
      new RegExp(emailValidation).test(val)
    );
  }, []);

  const handleAccountChange = useCallback(() => {
    // Editing invalidates any prior existence result.
    checkSeq.current += 1;
    setAccountStatus('unknown');
  }, []);

  const handleAccountBlur = useCallback(
    async (value) => {
      if (!isCompleteAccount(value)) {
        setAccountStatus('unknown');
        return;
      }
      const seq = (checkSeq.current += 1);
      try {
        const exists = await checkAccountExists(value);
        if (seq === checkSeq.current) {
          setAccountStatus(exists ? 'exists' : 'new');
        }
      } catch {
        if (seq === checkSeq.current) setAccountStatus('unknown');
      }
    },
    [isCompleteAccount]
  );

  const shouldHideFeatures = useMemo(() => {
    if (['qr_code', 'portal_qr_code'].includes(searchParams.get('utm_medium'))) {
      return true;
    }
    if (callbackUrl) {
      try {
        const url = new URL(callbackUrl);
        if (['qr_code', 'portal_qr_code'].includes(url.searchParams.get('utm_medium'))) {
          return true;
        }
        const pathMatch = url.pathname.match(/\/(?:applications?)\/([^/]+)/);
        if (pathMatch && pathMatch[1] && !pathMatch[1].match(/^00Q/)) {
          return true;
        }
      } catch {
        // Invalid URL, ignore
      }
    }
    return false;
  }, [searchParams, callbackUrl]);

  const showNoClinicMessage = useMemo(() => {
    if (!shouldHideFeatures) return false;

    if (searchParams.get('doctor')) {
      return false;
    }

    if (callbackUrl) {
      try {
        const url = new URL(callbackUrl);
        const pathMatch = url.pathname.match(/\/applications?\/([^/]+)/);
        if (pathMatch && pathMatch[1]) {
          const slug = pathMatch[1];
          if (!slug.match(/^00Q/) && slug !== 'apply') {
            return false;
          }
        }
      } catch {
        // Invalid URL, ignore
      }
    }

    return true;
  }, [shouldHideFeatures, searchParams, callbackUrl]);

  const primaryAction = showNoClinicMessage
    ? t('login.button.continue', 'Continue')
    : t('login.button.getstarted', 'Get Started');

  const buttonLabel =
    accountStatus === 'exists'
      ? t('login.button.signin', 'Sign In')
      : primaryAction;

  const financingFeatures = useMemo(
    () => [
      {
        icon: Speedometer,
        title: t(
          'login.new.deviceFinancing.features.fastApplication.title',
          'Fast Application'
        ),
        description: t(
          'login.new.deviceFinancing.features.fastApplication.description',
          'Complete your application online with basic business, equipment, and owner information.'
        )
      },
      {
        icon: BusinessOutline,
        title: t(
          'login.new.deviceFinancing.features.businessFirst.title',
          'Business-First Review'
        ),
        description: t(
          'login.new.deviceFinancing.features.businessFirst.description',
          'We review your business first and only ask for additional owner details when needed.'
        )
      }
    ],
    [t]
  );

  const [submitError, setSubmitError] = useState(null);

  const submitHandler = useCallback(
    async (data) => {
      setSubmitError(null);
      setIsLoading(true);
      let type;
      try {
        type = await checkLogin(data.account);
      } catch (err) {
        throw err;
      }
      if (type === 'credentials') {
        goToCodeSignIn(router, data.account);
      } else if (!isFinancing && type === 'partner') {
        const result = await partnerLogin();
        if (result === 'borrower') {
          newAccount(data.account);
        } else if (result === 'partner') {
          router.push('/partners');
        } else {
          setIsLoading(false);
        }
      } else if (!isFinancing && type === 'google') {
        return google();
      } else {
        setIsLoading(false);
      }
    },
    [router, partnerLogin, isFinancing, t]
  );

  return (
    <div className="flex flex-col gap-6">
      {isFinancing ? (
        <Link
          href="/"
          aria-label={t(
            'login.new.deviceFinancing.patientBanner.title',
            'Looking for patient financing?'
          )}
          className="w-full max-w-form-content mx-auto flex items-center gap-3 md:gap-4 bg-base-100 rounded-2xl md:rounded-[1.75rem] px-4 py-3 md:px-5 md:py-4 shadow-[0_12px_28px_rgba(0,0,0,0.12)] hover:shadow-[0_0_8px_rgba(0,0,0,0.2)] transition-shadow my-4"
        >
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center gap-2 md:gap-3">
              <img
                src="/images/person.svg"
                alt=""
                className="w-5 h-5 shrink-0"
                aria-hidden
              />
              <p className="text-sm md:text-base font-bold text-base-content tracking-tight">
                {t(
                  'login.new.deviceFinancing.patientBanner.title',
                  'Looking for patient financing?'
                )}
              </p>
            </div>
            <p className="text-xs md:text-sm text-muted mt-1">
              {t(
                'login.new.deviceFinancing.patientBanner.description',
                'This application is meant for businesses, not patients.'
              )}
            </p>
          </div>
          <ChevronForward
            className="w-5 h-5 shrink-0 text-base-content"
            aria-hidden
          />
        </Link>
      ) : (
        <div className="min-h-6" aria-hidden />
      )}
      <div className="text-center">
        <Heading
          hero
          heading={
            isFinancing
              ? t(
                  'login.new.deviceFinancing.heading',
                  'Get Approved for Equipment Financing'
                )
              : shouldHideFeatures
                ? t(
                    'login.new.headingNoClinic',
                    'Apply Now & Get an Instant Approval Decision'
                  )
                : t('login.new.heading', 'Get Approved for Monthly Payments')
          }
          subheading={
            isFinancing
              ? t(
                  'login.new.deviceFinancing.subheading',
                  'Apply in minutes and get a fast financing decision for your clinic or practice.'
                )
              : shouldHideFeatures
                ? t(
                    'login.new.subheadingNoClinic',
                    'Complete your application in under 2 minutes and get an instant decision with zero credit score impact.'
                  )
                : t(
                    'login.new.subheading',
                    'Complete your application to get an instant approval decision!'
                  )
          }
        />
      </div>
      <div className="flex flex-col gap-6">
        {isFinancing && (
          <div className="w-full">
            <Card>
              <Features items={financingFeatures} />
            </Card>
          </div>
        )}
        {!isFinancing && !shouldHideFeatures && (
          <div className="w-full">
            <Card>
              <Features items={signInFeatureItems(t)} />
            </Card>
          </div>
        )}
        <form
          onSubmit={handleSubmit(submitHandler)}
          className="flex flex-col gap-6"
        >
          <AccountField
            control={control}
            label={t('login.label.signup', 'Sign Up')}
            labelEnd={
              <span className="text-sm text-muted">
                {t('login.label.returning', 'Returning Borrower?')}{' '}
                <Link
                  href={`/auth/sign-in/returning${
                    searchParams.size ? `?${searchParams.toString()}` : ''
                  }`}
                  className="font-semibold text-base-content underline"
                >
                  {t('login.button.login', 'Log In')}
                </Link>
              </span>
            }
            onValueChange={handleAccountChange}
            onBlurValue={handleAccountBlur}
          />

          {showNoClinicMessage && (
            <div className="card-raised p-4 flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-beautifi-blue-50 flex items-center justify-center">
                <Bulb className="w-4 h-4 text-base-content" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-base-content mb-1 tracking-tight">
                  {t(
                    'login.new.noClinic.title',
                    "Didn't Choose a Clinic? No Worries!"
                  )}
                </p>
                <p className="text-sm text-muted">
                  {t(
                    'login.new.noClinic.description',
                    'You can continue your application without selecting a clinic and update your clinic later, even after submitting your application.'
                  )}
                </p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="btn btn-neutral beautifi-shadow w-full rounded-full"
          >
            {isLoading && <span className="loading loading-spinner"></span>}
            {buttonLabel}
          </Button>

          {(error || submitError) && (
            <div className="alert alert-error text-white rounded-field">
              <span>{error || submitError}</span>
            </div>
          )}

          <span className="flex items-center justify-center gap-1">
            <LockClosed className="w-4 h-4" />
            <span className="text-xs">
              {isFinancing
                ? t(
                    'login.label.securityDeviceFinancing',
                    'Bank-level security keeps your business and personal information safe and secure.'
                  )
                : t(
                    'login.label.security',
                    'Bank-level security keeps your information safe and secure.'
                  )}
            </span>
          </span>

          {!isFinancing && (
            <>
              <div className="divider my-1 text-xs mx-4 text-muted">OR</div>

              <GoogleButton
                disabled={isLoading}
                isLoading={googleLoading}
                onClick={() => {
                  setGoogleLoading(true);
                  google();
                }}
              >
                {t('login.button.google', 'Continue with Google')}
              </GoogleButton>
            </>
          )}

          <div className="text-xs text-center text-muted">
            {isFinancing ? (
              <Trans
                t={t}
                i18nKey="login.agreement.deviceFinancing"
                values={{ primaryAction: buttonLabel }}
              >
                By clicking "{{ primaryAction }}," you agree to Beautifi's{' '}
                <TermsLink key="terms">Terms of Service</TermsLink> and{' '}
                <PrivacyLink key="privacy">Privacy Policy</PrivacyLink> and
                consent to receive communications related to your equipment
                financing application.
              </Trans>
            ) : (
              <Trans
                t={t}
                i18nKey="login.agreement.full"
                values={{ primaryAction: buttonLabel }}
              >
                By clicking “{{ primaryAction }}” or “Continue with Google” you
                agree to Beautifi’s{' '}
                <TermsLink key="terms">Terms of Service</TermsLink> and{' '}
                <PrivacyLink key="privacy">Privacy Policy</PrivacyLink>, and
                consent to receive marketing communications from Beautifi and/or
                its affiliates.
              </Trans>
            )}
          </div>
        </form>
      </div>
      {/* Keeps the disclaimer clear of the wave at the bottom of .waves */}
      <div className="min-h-8" aria-hidden />
    </div>
  );
}
